import { Inject, Injectable } from '@angular/core';
import Dexie, { liveQuery } from 'dexie';
import { from, Observable } from 'rxjs';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { EncryptionService } from '@evorait/pace-encryption';
import { ConcurrentUsageDbData, ConcurrentUsageEntryState } from './concurrent-usage.type';
import { ConcurrentStorageData } from '../../types/concurrent-usage';
import { isDefined } from '../../utilities/is-defined';

@Injectable({
    providedIn: 'root',
})
export class ConcurrentUsageDbService extends Dexie {
    public static readonly DB_NAME = 'ConcurrentUsageDatabase';
    public static readonly DB_TABLE = 'concUsageData';
    public static readonly DB_KEY_PATH = 'key';

    private static readonly VERSION_ONE = 1;

    private concUsageData: Dexie.Table<ConcurrentUsageDbData, string>;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly encryption: EncryptionService,
    ) {
        super(ConcurrentUsageDbService.DB_NAME);
        this.version(ConcurrentUsageDbService.VERSION_ONE).stores({
            [ConcurrentUsageDbService.DB_TABLE]: `${ConcurrentUsageDbService.DB_KEY_PATH},deleted`,
        });
    }

    public async bulkAdd(items: ConcurrentStorageData[]): Promise<void> {
        const encryptedItems: ConcurrentUsageDbData[] = await Promise.all(
            items.map(async (item) => {
                return {
                    key: item.key,
                    deleted: 0,
                    timestamp: item.timestamp ?? Date.now(),
                    payload: await this.encryption.encryptAsync(item.value),
                };
            }),
        );

        await this.concUsageData.bulkAdd(encryptedItems);
    }

    public async getPresentEntries(): Promise<ConcurrentStorageData[]> {
        const allEntries = await this.concUsageData
            .where('deleted')
            .equals(ConcurrentUsageEntryState.PRESENT)
            .toArray();

        return Promise.all(
            allEntries.map(async (entry) => {
                return {
                    key: entry.key,
                    timestamp: entry.timestamp,
                    value: await this.encryption.decryptAsync(entry.payload),
                };
            }),
        );
    }

    public async getDeletedEntries(): Promise<string[]> {
        const allEntries = await this.concUsageData
            .where('deleted')
            .equals(ConcurrentUsageEntryState.DELETED)
            .toArray();
        return allEntries.map((entry) => entry.key);
    }

    public async put(payload: ConcurrentStorageData): Promise<boolean> {
        const encryptedPayload = await this.encryption.encryptAsync(payload.value);

        const data: ConcurrentUsageDbData = {
            key: payload.key,
            deleted: ConcurrentUsageEntryState.PRESENT,
            timestamp: payload.timestamp,
            payload: encryptedPayload,
        };

        await this.concUsageData.put(data);

        return true;
    }

    public async get(key: string): Promise<ConcurrentStorageData | null> {
        const entry = await this.concUsageData.get(key);
        if (!this.isExistingDbEntry(entry)) {
            return null;
        }

        return {
            key: entry.key,
            timestamp: entry.timestamp,
            value: await this.encryption.decryptAsync(entry.payload),
        };
    }

    // TODO: What about the previously thrown errors?
    //       --> replace with logic that creates expected data not on error, but on event with payload 'undefined'
    public liveQuery$(key: string): Observable<ConcurrentStorageData | undefined> {
        return from(
            liveQuery(() =>
                this.concUsageData
                    .where(ConcurrentUsageDbService.DB_KEY_PATH)
                    .equals(key) // key is unique in the db table. this means there is only one entry in the returned array.
                    .first() // first returns the first entry from above array
                    .then((entry) =>
                        !this.isExistingDbEntry(entry)
                            ? undefined
                            : this.encryption.decryptAsync(entry.payload).then(
                                  (payload) =>
                                      ({
                                          key: entry.key,
                                          timestamp: entry.timestamp,
                                          value: payload,
                                      }) as ConcurrentStorageData,
                              ),
                    )
                    .catch((error: Error) => {
                        this.log.error(
                            `ConcurrentUsageDbService: liveQuery$: unknown error occurred when reading ${key} from db: ${error}`,
                        );
                        return undefined;
                    }),
            ),
        );
    }

    public async remove(key: string): Promise<boolean> {
        this.log.debug('Marking concurrent usage data for deletion', { key });
        const result = await this.concUsageData.update(key, { deleted: ConcurrentUsageEntryState.DELETED });
        return result > 0;
    }

    public async clear(): Promise<void> {
        await this.concUsageData.clear();
    }

    private isExistingDbEntry(entry: ConcurrentUsageDbData | undefined): entry is ConcurrentUsageDbData {
        return isDefined(entry?.payload) && entry.deleted === ConcurrentUsageEntryState.PRESENT;
    }
}
